import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import sharedMessages from '../../messages';
import useAsync from 'hooks/useAsync';
import Spacer from 'components/Spacer';
import useResendCooldown from 'hooks/useResendCooldown';
import { FormikHelpers } from 'formik';
import { Button, TextField, TextLink } from '@teachstonellc/component-library';
import { sendSmsCodeForLogin } from 'actions/sms';
import { toastError, toastSuccess } from 'components/Toast';
import { Formik, Form, PhoneInput } from 'components/EnhancedForm';
import { useLoginManager } from 'components/LoginManager';
import { useId, useRef } from 'react';
import useFocusOnStepChange from '../../useFocusOnStepChange';
import { formatLoginIdentifier, joinIds } from '../../loginInput';

import {
  getGenericErrorMessage,
  getLockedMessage,
  isAlreadyAuthenticatedError,
  redirectToDashboard
} from '../../utils';

import {
  ButtonWrapper,
  ButtonsWrapper,
  ResendWrapper,
  TextButtonWrapper
} from './Styled';

const validationSchema = yup.object({
  code: yup.string().required(() => i18n.ft(messages.errors.code.required))
});

function VerifyCodeForm() {
  const resendRequest = useAsync();
  const { canResend, secondsLeft, startCooldown } = useResendCooldown({
    startOnMount: true
  });
  const sentToId = useId();
  const codeErrorId = useId();
  const codeInputRef = useRef<HTMLInputElement>(null);
  useFocusOnStepChange(codeInputRef);
  const { setStep, emailOrPhone, submitForm } = useLoginManager();

  function handleSubmit(values: any, formik: FormikHelpers<any>) {
    submitForm({ user: values }, formik);
  }

  function handleResendCode() {
    if (!canResend) {
      return;
    }

    startCooldown();
    resendRequest.run(sendSmsCodeForLogin({ phone_number: emailOrPhone }), {
      onSuccess: () => {
        toastSuccess({ description: i18n.ft(messages.codeSent) });
      },
      onError: (error: any) => {
        if (isAlreadyAuthenticatedError(error)) {
          redirectToDashboard();
          return;
        }

        toastError({
          description: getLockedMessage(error) ?? getGenericErrorMessage()
        });
      }
    });
  }

  function handleBackToLogin() {
    setStep('login');
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      initialValues={{ login_phone: emailOrPhone, code: '' }}
      validateOnBlur={false}
    >
      {formik => {
        const isCodeError = Boolean(formik.touched.code && formik.errors.code);

        return (
          <Form>
            <p className="mb-4 text-left">{i18n.ft(messages.codeSentHint)}</p>

            <PhoneInput
              name="login_phone"
              label={i18n.ft(messages.fields.phone)}
              readOnly
            />

            <Spacer size={8} />
            <TextField
              {...formik.getFieldProps('code')}
              ref={codeInputRef}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              label={i18n.ft(messages.fields.code)}
              placeholder={i18n.ft(messages.fields.codePlaceholder)}
              errorId={codeErrorId}
              aria-describedby={joinIds(sentToId, isCodeError && codeErrorId)}
              error={isCodeError ? formik.errors.code : undefined}
            />
            <span id={sentToId} className="sr-only">
              {i18n.ft(messages.codeSentTo, {
                phone: formatLoginIdentifier(emailOrPhone)
              })}
            </span>

            <ButtonsWrapper>
              <ButtonWrapper>
                <Button
                  fullWidth
                  variant="primary"
                  shape="rounded"
                  type="submit"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                >
                  {i18n.ft(messages.submit)}
                </Button>
              </ButtonWrapper>

              <ResendWrapper>
                <ButtonWrapper>
                  <Button
                    fullWidth
                    variant="secondary"
                    shape="rounded"
                    type="button"
                    onClick={handleResendCode}
                    loading={resendRequest.isLoading}
                    disabled={resendRequest.isLoading}
                    aria-disabled={!canResend || undefined}
                  >
                    {canResend
                      ? i18n.ft(messages.resend)
                      : i18n.ft(messages.resendCountdown, {
                          seconds: secondsLeft
                        })}
                  </Button>
                </ButtonWrapper>
              </ResendWrapper>
            </ButtonsWrapper>

            <TextButtonWrapper>
              <TextLink as="button" type="button" onClick={handleBackToLogin}>
                {i18n.ft(sharedMessages.useDifferentLogin)}
              </TextLink>
            </TextButtonWrapper>
          </Form>
        );
      }}
    </Formik>
  );
}

export default VerifyCodeForm;
